import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CardService } from '../services/cardService';
import { ExpenseService } from '../services/expenseService';
import type { ExpenseAdminDetailResponseData } from '../types/expense';
import type { MaxRateCard } from '../types/cardRecommendation';

const ExpenseDetail: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userId } = useParams();
  const [searchParams] = useSearchParams();

  const isAdmin = user?.role === 'admin';

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ExpenseAdminDetailResponseData | null>(null);

  const [recommendedLoading, setRecommendedLoading] = useState<boolean>(false);
  const [recommendedError, setRecommendedError] = useState<string | null>(null);
  const [recommendedCard, setRecommendedCard] = useState<MaxRateCard | null>(null);

  const versionParamRaw = searchParams.get('version');
  const versionParam = versionParamRaw ? Number(versionParamRaw) : undefined;
  const version = Number.isFinite(versionParam) ? versionParam : undefined;

  const defaultSearchAmount = 5000;
  const defaultSearchFrequency = 'monthly';

  const amountParamRaw = searchParams.get('amount');
  const frequencyParamRaw = searchParams.get('frequency');

  const getInitialAmount = () => {
    const n = amountParamRaw ? Number(amountParamRaw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : defaultSearchAmount;
  };

  const getInitialFrequency = () => {
    const f = (frequencyParamRaw || '').trim();
    return f || defaultSearchFrequency;
  };

  const [searchAmountInput, setSearchAmountInput] = useState<string>(() => String(getInitialAmount()));
  const [searchFrequency, setSearchFrequency] = useState<string>(() => getInitialFrequency());

  useEffect(() => {
    const nextAmount = String(getInitialAmount());
    const nextFrequency = getInitialFrequency();
    setSearchAmountInput((prev) => (prev === nextAmount ? prev : nextAmount));
    setSearchFrequency((prev) => (prev === nextFrequency ? prev : nextFrequency));
  }, [amountParamRaw, frequencyParamRaw]);

  useEffect(() => {
    document.title = 'Expense Detail';
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    if (!userId) return;

    let isMounted = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await ExpenseService.getExpenseDetailAdmin(userId, { version });
        if (!isMounted) return;
        setData(res.data);
      } catch (err) {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load expense detail');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAdmin, userId, version]);

  useEffect(() => {
    if (!isAdmin) return;

    let isMounted = true;

    (async () => {
      try {
        setRecommendedLoading(true);
        setRecommendedError(null);
        const res = await CardService.getMaxRateCard();
        if (!isMounted) return;
        setRecommendedCard(res.data.card);
      } catch (err) {
        if (!isMounted) return;
        setRecommendedError(err instanceof Error ? err.message : 'Failed to load recommended card');
      } finally {
        if (!isMounted) return;
        setRecommendedLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  const formatDate = (iso: string | undefined | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  const items = useMemo(() => data?.items || [], [data]);
  const userCards = useMemo(() => data?.userCards || [], [data]);

  const recommendedTopCategories = useMemo(
    () => (recommendedCard?.categories || []).slice(0, 3),
    [recommendedCard]
  );
  const recommendedBestRate = useMemo(
    () => (recommendedCard?.earnRates?.other && recommendedCard.earnRates.other.length > 0 ? recommendedCard.earnRates.other[0] : null),
    [recommendedCard]
  );

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-500 mb-4">You need admin privileges to access Expenses.</p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading expense detail...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expense Detail</h1>
          <p className="mt-1 text-sm text-gray-500">
            {data?.user?.name || '—'} • {data?.user?.email || '—'}
          </p>
        </div>

        <button
          onClick={() => navigate('/expenses')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>
      )}

      {data && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Version</div>
              <div className="text-sm font-medium text-gray-900">{data.expense?.version ?? '—'}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Questions</div>
              <div className="text-sm font-medium text-gray-900">{data.questionCount ?? '—'}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Created</div>
              <div className="text-sm font-medium text-gray-900">{formatDate(data.expense?.createdAt)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Updated</div>
              <div className="text-sm font-medium text-gray-900">{formatDate(data.expense?.updatedAt)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Monthly Expenses</div>
              <div className="text-sm font-medium text-gray-900">
                {typeof data.expense?.monthlyExpenses === 'number'
                  ? data.expense.monthlyExpenses
                  : data.expense?.monthlyExpenses
                    ? 'Yes'
                    : '—'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Recommended Card</h2>
          <p className="mt-1 text-xs text-gray-500">Top card suggestion (max reward rate)</p>
        </div>
        <div className="p-4">
          {recommendedError && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{recommendedError}</div>
          )}

          {recommendedLoading && !recommendedCard && (
            <div className="text-center py-6">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              <p className="mt-2 text-gray-500 text-sm">Loading recommended card...</p>
            </div>
          )}

          {recommendedCard && (
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900">{recommendedCard.name}</div>
                  <div className="mt-1 text-sm text-gray-600">{recommendedCard.bankName}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {(Number(recommendedCard.maxRate) * 100).toFixed(2)}%
                    </span>
                    {recommendedTopCategories.map((c) => (
                      <span key={c} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {c}
                      </span>
                    ))}
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-500">Annual fee</div>
                      <div className="mt-1 text-sm font-medium text-gray-900">
                        ₹{recommendedCard.annualFees?.primary?.toLocaleString('en-IN') ?? '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-500">Base earn rate</div>
                      <div className="mt-1 text-sm font-medium text-gray-900">
                        {recommendedCard.earnRates?.default
                          ? `${recommendedCard.earnRates.default.per} ${recommendedCard.earnRates.default.unit} / ₹${recommendedCard.earnRates.default.perAmount}`
                          : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-500">Best rate</div>
                      <div className="mt-1 text-sm font-medium text-gray-900">
                        {recommendedBestRate
                          ? `${recommendedBestRate.category} — ${recommendedBestRate.per} ${recommendedBestRate.unit} / ₹${recommendedBestRate.perAmount}`
                          : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="shrink-0">
                  <button
                    onClick={() =>
                      navigate('/cards/recommended', {
                        state: { card: recommendedCard },
                      })
                    }
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    View Card Details
                  </button>
                </div>
              </div>
            </div>
          )}

          {!recommendedLoading && !recommendedCard && !recommendedError && (
            <div className="text-sm text-gray-500">No recommendation available.</div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">User Cards</h2>
          <p className="mt-1 text-xs text-gray-500">Cards saved by this user</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Card</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Point</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {userCards.map((uc) => (
                <tr key={uc._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{uc.card?.name || '—'}</div>
                    <div className="text-sm text-gray-500">{uc.card?.slug || uc.card?.id || '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{uc.card?.bankName || '—'}</div>
                    <div className="text-sm text-gray-500">{uc.card?.bankSlug || '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {uc.status || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{typeof uc.pointsCount === 'number' ? uc.pointsCount : '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{typeof uc.totalPoints === 'number' ? uc.totalPoints : '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(uc.lastPointAt)}</div>
                  </td>
                </tr>
              ))}

              {!loading && userCards.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                    No user cards found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Items</h2>
            {version ? (
              <p className="mt-1 text-xs text-gray-500">Showing requested version: {version}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">Showing latest version (no version query param)</p>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700">Amount</label>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={searchAmountInput}
                onChange={(e) => setSearchAmountInput(e.target.value)}
                className="mt-1 w-40 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700">Frequency</label>
              <select
                value={searchFrequency}
                onChange={(e) => setSearchFrequency(e.target.value)}
                className="mt-1 w-44 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="half_yearly">Half Yearly</option>
                <option value="annually">Annually</option>
                <option value="one-time">One-time</option>
              </select>
            </div>

            <button
              type="button"
              onClick={() => {}}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Search
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-64 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
                <th className="px-4 py-3 w-52 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 w-44 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 w-32 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                <th className="px-4 py-3 w-32 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((it, idx) => {
                const q = it.question;
                const questionText =
                  (q && (q.title || q.question || q.text || q.label)) || (it.questionId ? `Question ${it.questionId}` : '—');

                const questionSearch =
                  q && typeof (q as any).search === 'string'
                    ? ((q as any).search as string)
                    : q && typeof (q as any).query === 'string'
                      ? ((q as any).query as string)
                      : '';

                const points: any = it.points;
                const pointsIsObject = points && typeof points === 'object' && !Array.isArray(points);
                const name = pointsIsObject ? (points.name ?? '—') : '—';
                const category = pointsIsObject ? (points.category ?? '—') : '—';
                const frequency = pointsIsObject ? (points.frequency ?? '—') : '—';
                const amount = pointsIsObject ? (points.amount ?? '—') : points ? String(points) : '—';

                const searchQueryRaw =
                  questionSearch ||
                  (pointsIsObject && (typeof points.name === 'string' ? points.name : typeof points.category === 'string' ? points.category : '')) ||
                  (typeof questionText === 'string' ? questionText : String(questionText));
                const searchQuery = searchQueryRaw.trim();

                const rewardSearchHref = (() => {
                  if (!userId) return null;
                  if (!searchQuery) return null;

                  const amountForSearch = (() => {
                    const n = Number(searchAmountInput);
                    return Number.isFinite(n) && n > 0 ? n : defaultSearchAmount;
                  })();
                  const frequencyForSearch = (searchFrequency || '').trim() || defaultSearchFrequency;

                  const params = new URLSearchParams();
                  params.set('q', searchQuery);
                  params.set('question', String(questionText));
                  params.set('amount', String(amountForSearch));
                  params.set('frequency', frequencyForSearch);

                  return `/expenses/${encodeURIComponent(userId)}/rewards?${params.toString()}`;
                })();

                return (
                  <tr key={`${it.key || it.questionId || 'item'}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900 break-all">{it.key || '—'}</div>
                    </td>
                    <td className="px-4 py-4">
                      {rewardSearchHref ? (
                        <a
                          href={rewardSearchHref}
                          className="block text-left text-sm text-blue-600 hover:text-blue-800 hover:underline break-words"
                          title="Search reward options"
                        >
                          {String(questionText)}
                        </a>
                      ) : (
                        <span className="block text-left text-sm text-gray-500 break-words" title="Search reward options">
                          {String(questionText)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 break-words">{String(name)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 break-words">{String(category)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 break-words">{String(frequency)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 break-words">{String(amount)}</div>
                    </td>
                  </tr>
                );
              })}

              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                    No items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExpenseDetail;
